import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/** PFC-9 — the Fiscal cabinet read API. */

export type FiscalDocumentType = 'store' | 'prro' | 'cashier' | 'query';
export type FiscalSubmissionStatus = 'draft' | 'sent' | 'kvt1' | 'kvt2' | 'rejected';
export type FiscalSignatureOutcome = 'signed' | 'failed';

export type FiscalCabinetAvailability = {
  available: boolean;
  reason: 'ua-country-pack-disabled' | null;
};

export type FiscalSubmission = {
  id: string;
  documentType: FiscalDocumentType;
  tin: string;
  formCode: string;
  payloadSha256: string;
  fileName: string;
  documentCounter: number;
  periodYear: number;
  periodMonth: number;
  periodType: number;
  regionCode: string;
  status: FiscalSubmissionStatus;
  dpsDocumentId: string | null;
  createdBy: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FiscalSignatureAuditRow = {
  id: string;
  createdAt: string;
  actorUserId: string | null;
  operation: 'sign' | 'seal';
  documentType: FiscalDocumentType;
  documentSha256: string;
  keySerial: string | null;
  outcome: FiscalSignatureOutcome;
  errorCode: string | null;
  errorMessage: string | null;
  submissionId: string | null;
};

export type Paged<T> = { items: T[]; total: number; page: number; pageSize: number };

export const DOCUMENT_TYPE_LABEL_KEY: Record<FiscalDocumentType, string> = {
  store: 'fiscalCabinet.documentType.store',
  prro: 'fiscalCabinet.documentType.prro',
  cashier: 'fiscalCabinet.documentType.cashier',
  query: 'fiscalCabinet.documentType.query',
};
export const DOCUMENT_TYPE_LABEL: Record<FiscalDocumentType, string> = {
  store: 'Store',
  prro: 'PRRO',
  cashier: 'Cashier',
  query: 'Query',
};

export const SUBMISSION_STATUS_LABEL_KEY: Record<FiscalSubmissionStatus, string> = {
  draft: 'fiscalCabinet.status.draft',
  sent: 'fiscalCabinet.status.sent',
  kvt1: 'fiscalCabinet.status.kvt1',
  kvt2: 'fiscalCabinet.status.kvt2',
  rejected: 'fiscalCabinet.status.rejected',
};
export const SUBMISSION_STATUS_LABEL: Record<FiscalSubmissionStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  kvt1: 'Receipt 1',
  kvt2: 'Receipt 2',
  rejected: 'Rejected',
};

export const OPERATION_LABEL_KEY: Record<FiscalSignatureAuditRow['operation'], string> = {
  sign: 'fiscalCabinet.operation.sign',
  seal: 'fiscalCabinet.operation.seal',
};
export const OPERATION_LABEL: Record<FiscalSignatureAuditRow['operation'], string> = {
  sign: 'Signature',
  seal: 'Signed and encrypted',
};

export const OUTCOME_LABEL_KEY: Record<FiscalSignatureOutcome, string> = {
  signed: 'fiscalCabinet.outcome.signed',
  failed: 'fiscalCabinet.outcome.failed',
};
export const OUTCOME_LABEL: Record<FiscalSignatureOutcome, string> = {
  signed: 'Signed',
  failed: 'Failed',
};

export function useFiscalCabinetAvailability(enabled = true) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'availability'] as const,
    enabled,
    queryFn: async (): Promise<FiscalCabinetAvailability> =>
      (await apiClient.get<FiscalCabinetAvailability>('/fiscal-cabinet/availability')).data,
  });
}

export function useFiscalSubmissions(query: { page: number; pageSize: number }, enabled: boolean) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'submissions', query] as const,
    enabled,
    queryFn: async (): Promise<Paged<FiscalSubmission>> =>
      (
        await apiClient.get<Paged<FiscalSubmission>>('/fiscal-cabinet/submissions', {
          params: query,
        })
      ).data,
  });
}

export function useFiscalSignatureAudit(
  query: { page: number; pageSize: number },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['fiscal-cabinet', 'signature-audit', query] as const,
    enabled,
    queryFn: async (): Promise<Paged<FiscalSignatureAuditRow>> =>
      (
        await apiClient.get<Paged<FiscalSignatureAuditRow>>('/fiscal-cabinet/signature-audit', {
          params: query,
        })
      ).data,
  });
}
